import React, { useMemo, useState } from "react";
import { showAlert } from "../../../utils/showAlert";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { createRoom } from "../../../services/roomsService";
import type RootStackParamList from "../../../navigation/RootStackParamList";
import CreateRoomView from "../ui/CreateRoomView";
import { CreateRoomStep } from "../createRoom.types";
import {
  logCreateRoom,
  markUserCreatedRoom,
} from "../../../utils/analyticsUtils";
import { buildLocalDateTime } from "../../../utils/meetingAvailability";

type Props = NativeStackScreenProps<RootStackParamList, "Schedule">;

export default function CreateRoomContainer({ navigation }: Props) {
  const [currentStep, setCurrentStep] = useState<CreateRoomStep>(1);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [selectedHour, setSelectedHour] = useState("");
  const [selectedMinute, setSelectedMinute] = useState("");
  const [selectedAmPm, setSelectedAmPm] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());

  // Topics
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);

  // Meeting name
  const [roomName, setRoomName] = useState("");
  const [selectedMeetingNameOption, setSelectedMeetingNameOption] = useState(
    "Custom / Make your own...",
  );
  const [maxParticipants, setMaxParticipants] = useState<number>(3);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Room name validation
  const [roomNameError, setRoomNameError] = useState<string | null>(null);

  // Get current timezone for display
  const currentTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  const toggleTopic = (id: string) => {
    setSelectedTopicIds((prev) =>
      prev.includes(id)
        ? prev.filter((topicId) => topicId !== id)
        : [...prev, id],
    );
  };

  // Handle room name change and clear error
  const handleRoomNameChange = (text: string) => {
    setRoomName(text);
    if (roomNameError) {
      setRoomNameError(null);
    }
  };

  /**
   * Submit:
   * 1) Call createRoom on backend.
   * 2) Navigate to Call with channelId from backend response.
   * IMPORTANT: RootStackParamList.Call must be { channelId: string; name: string }
   */
  const handleCreateAndJoin = async () => {
    // Validate room name
    const finalRoomName =
      selectedMeetingNameOption === "Custom / Make your own..."
        ? roomName.trim()
        : selectedMeetingNameOption;

    if (!finalRoomName) {
      setRoomNameError("You need to enter a meeting name");
      return;
    }

    if (selectedTopicIds.length === 0) {
      return;
    }

    if (!selectedHour || !selectedMinute || !selectedAmPm) {
      showAlert(
        "Validation",
        "Please choose one of the available meeting time slots.",
      );
      return;
    }

    const startAt = buildLocalDateTime(
      selectedDate,
      selectedHour,
      selectedMinute,
      selectedAmPm,
    );
    const startAtIso = startAt.toISOString();

    const payload = {
      name: finalRoomName,
      startAt: startAtIso,
      topics: selectedTopicIds,
      maxParticipants,
    };

    setIsSubmitting(true);
    try {
      const response = await createRoom(payload);

      if (!response.success) {
        showAlert("Error", response.error ?? "Failed to create a room.");
        return;
      }

      const channelIdFromResponse = response.channelId ?? response.roomId;

      if (!channelIdFromResponse) {
        console.error("[createRoom] Invalid response:", response);
        showAlert("Error", "Room was created but channelId is missing.");
        return;
      }

      // Log room creation
      logCreateRoom(roomName.trim(), selectedTopicIds);
      markUserCreatedRoom();

      navigation.navigate("MeetingsTabs");
    } catch (error: any) {
      console.error("[createRoom] Unexpected error:", error);
      showAlert("Error", error?.message ?? "Failed to create a room.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CreateRoomView
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      agreedToTerms={agreedToTerms}
      onAgreeToTerms={() => setAgreedToTerms(true)}
      selectedHour={selectedHour}
      selectedMinute={selectedMinute}
      selectedAmPm={selectedAmPm}
      selectedDate={selectedDate}
      selectedTopicIds={selectedTopicIds}
      roomName={roomName}
      isSubmitting={isSubmitting}
      roomNameError={roomNameError}
      currentTimezone={currentTimezone}
      onChangeHour={setSelectedHour}
      onChangeMinute={setSelectedMinute}
      onChangeAmPm={setSelectedAmPm}
      onChangeDate={setSelectedDate}
      onToggleTopic={toggleTopic}
      onChangeRoomName={handleRoomNameChange}
      onSubmit={handleCreateAndJoin}
      onCancel={() => navigation.pop(2)}
      onBack={() => navigation.pop(2)}
      selectedMeetingNameOption={selectedMeetingNameOption}
      onChangeSelectedMeetingNameOption={setSelectedMeetingNameOption}
      maxParticipants={maxParticipants}
      onChangeMaxParticipants={setMaxParticipants}
    />
  );
}
